import React, { useEffect, useRef, useState } from 'react';
import './LoginGate.css';
import GoogleLoginButton from '../GoogleLogin/GoogleLogin';
import LastUsedOption from '../LastUsedBadge/LastUsedBadge';
import { lastSignInMethod } from '../../services/lastSignInMethod';
import { storageService } from '../../services/storage.service';
import { storeService } from '../../services/store.service';
import { SIGN_IN_REFUSED, SIGN_UP_SENT } from '../../services/signInRefusal';
import {
  emailLogin,
  emailRegister,
  forgotPassword,
  resendVerification,
  phoneSendCode,
  phoneVerifyCode,
  phoneChooseClient,
  type PhoneClientChoice,
  phoneCompleteSignup,
} from '../../services/auth.service';

type View = 'google' | 'email-login' | 'register' | 'forgot-password' | 'phone' | 'phone-otp' | 'phone-details' | 'phone-choose';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

// Group an AU mobile for display: "0434141631" -> "0434 141 631",
// "+61434141631" -> "+61 434 141 631". Falls back to the raw input for
// anything that doesn't look like a 10-digit national / +61 number.
const formatMobileDisplay = (raw: string): string => {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+61') && digits.length === 11) {
    return `+61 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  if (digits.length === 10 && digits.startsWith('0')) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return trimmed;
};

interface LoginGateProps {
  isOpen: boolean;
  onClose: () => void;
  currentStep?: string;
  showAppointmentMessage?: boolean;
  onLoginSuccess?: () => void;
  /**
   * View to land on when the gate opens. Contexts that already know how the
   * user should sign in (e.g. the post-payment summary's "log in with the
   * mobile number you used to book" sheet) can skip the method chooser.
   * The back arrow still returns to the chooser.
   */
  initialView?: View;
}

const LoginGate: React.FC<LoginGateProps> = ({
  isOpen,
  onClose,
  currentStep,
  showAppointmentMessage = false,
  onLoginSuccess,
  initialView = 'google',
}) => {
  /*
   * A sign-in here reloads the page in place, so the person lands where
   * they already are. `pre_login_step` is the Google redirect's way back
   * and means nothing for this path — left behind, the home page's init
   * read it later and bounced straight back into the booking flow.
   */
  const finishLogin = () => {
    storageService.removeItem('pre_login_step');
    onClose();
    onLoginSuccess?.();
    window.location.reload();
  };

  const [view, setView] = useState<View>(initialView);
  // The way this device last signed in, marked "Last used" (G1's exception).
  const [lastMethod] = useState(lastSignInMethod);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // email-login fields
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState('');

  // register fields
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  // forgot-password field
  const [fpEmail, setFpEmail] = useState('');

  // phone (SMS OTP) fields
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [resendIn, setResendIn] = useState(0);
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

  // phone sign-up ("Nearly there") fields — populated when verify-code says
  // the number is new; the token authorises complete-signup for 15 minutes.
  const [signupToken, setSignupToken] = useState('');

  // "Which of you is this?" — a number several people answer to. The code
  // proved the caller holds the phone, not which of them they are, so the
  // ticket below buys a session only once they say (#904).
  const [choiceToken, setChoiceToken] = useState('');
  const [choices, setChoices] = useState<PhoneClientChoice[]>([]);
  const [suFirstName, setSuFirstName] = useState('');
  const [suLastName, setSuLastName] = useState('');
  const [suEmail, setSuEmail] = useState('');
  // "Text me offers from {salon}" (MKT 16b, G27): never ticked for them.
  const [suOfferTexts, setSuOfferTexts] = useState(false);
  const salonName = storeService.getCompanyName() || 'this salon';

  // Resend countdown while the OTP screen is up.
  useEffect(() => {
    if (view !== 'phone-otp' || resendIn <= 0) return;
    const timer = setInterval(() => setResendIn(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [view, resendIn]);

  const resetState = () => {
    setError('');
    setSuccessMsg('');
    setUnverifiedEmail('');
  };

  // Re-apply the entry view each time the gate opens so a dismissed gate
  // reopens where its context expects (and stale errors don't linger).
  useEffect(() => {
    if (isOpen) {
      resetState();
      setView(initialView);
      // Each opening starts unticked: never a tick left from before (G27).
      setSuOfferTexts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialView]);

  const switchView = (v: View) => {
    resetState();
    setView(v);
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await emailLogin(loginEmail, loginPassword);
      finishLogin();
    } catch (err: any) {
      if (err.message === 'LOGIN_UNVERIFIED') {
        setUnverifiedEmail(loginEmail);
        setError('EMAIL_UNVERIFIED');
      } else if (err.message === 'LOGIN_REFUSED' || err.message === 'LOGIN_LOCKED') {
        // One answer whoever it is (G45): it never says whether the email
        // has an account, or whether a password was set.
        setError(err.serverMessage || SIGN_IN_REFUSED);
      } else {
        setError('Sign in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await emailRegister(regFirstName, regLastName, regEmail, regPassword);
      setSuccessMsg(SIGN_UP_SENT);
    } catch (err: any) {
      if (err.message === 'EMAIL_EXISTS') {
        setError('This email is already registered. Try signing in instead.');
      } else if (err.message === 'REGISTER_EMAIL_UNSENT') {
        // Nothing was kept (mpgadmin G104), so the same form works again.
        setError(err.serverMessage || `We couldn't send an email to ${regEmail}. Check it and try again.`);
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(fpEmail);
      setSuccessMsg("If this email exists, you'll receive a reset link shortly.");
    } catch {
      setSuccessMsg("If this email exists, you'll receive a reset link shortly.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    try {
      await resendVerification(unverifiedEmail);
      setError('');
      setSuccessMsg('Verification email resent! Check your inbox.');
    } catch {
      setError('Could not resend verification email. Please try again.');
    }
  };

  const requestPhoneCode = async () => {
    setError('');
    setLoading(true);
    try {
      await phoneSendCode(phoneNumber);
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      setResendIn(RESEND_COOLDOWN_SECONDS);
      setView('phone-otp');
      // Focus the first box once the OTP view has rendered.
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } catch (err: any) {
      if (err.message === 'PHONE_CODE_COOLDOWN') {
        // A code was sent moments ago — reuse it rather than blocking.
        setOtpDigits(Array(OTP_LENGTH).fill(''));
        setResendIn(err.retryAfterSeconds ?? RESEND_COOLDOWN_SECONDS);
        setView('phone-otp');
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
      } else if (err.message === 'PHONE_CODE_CAPACITY') {
        // Hourly send cap — no code went out, so stay put and say so rather
        // than sending them to a code screen that will never fill.
        setError(err.serverMessage || 'Too many codes requested just now. Please try again shortly, or continue with email.');
      } else {
        // Prefer the backend's explanation (e.g. SMS unavailable, use email)
        // over a generic retry prompt.
        setError(err.serverMessage || 'Could not send the code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await requestPhoneCode();
  };

  const verifyOtp = async (code: string) => {
    setError('');
    setLoading(true);
    try {
      const result = await phoneVerifyCode(phoneNumber, code);
      if (result.isNewUser && result.signupToken) {
        // Number verified but no account yet — collect their name.
        setSignupToken(result.signupToken);
        setError('');
        setView('phone-details');
        return;
      }
      if (result.choices && result.choices.length > 1 && result.choiceToken) {
        // Several people share this number — ask, never pick.
        setChoices(result.choices);
        setChoiceToken(result.choiceToken);
        setError('');
        setView('phone-choose');
        return;
      }
      finishLogin();
    } catch (err: any) {
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      otpRefs.current[0]?.focus();
      setError(
        err.message === 'PHONE_CODE_INVALID'
          ? 'Invalid or expired code. Please try again.'
          : 'Verification failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * "It's me." Redeems the one-shot ticket for a session as the person they
   * picked. An expired ticket means starting from the number again — the
   * code it came from has already been spent.
   */
  const chooseClient = async (clientId: number) => {
    setError('');
    setLoading(true);
    try {
      await phoneChooseClient(phoneNumber, choiceToken, clientId);
      finishLogin();
    } catch {
      setError('That took too long — please request a new code.');
      setView('phone');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await phoneCompleteSignup(phoneNumber, signupToken, suFirstName.trim(), suLastName.trim(), suEmail.trim(), suOfferTexts);
      finishLogin();
    } catch (err: any) {
      if (err.message === 'PHONE_SIGNUP_EXPIRED') {
        // The 15-minute sign-up window lapsed (or the token was already
        // used) — restart from the number screen with a fresh code.
        setSignupToken('');
        setView('phone');
        setError('That took a little too long — please verify your number again.');
      } else if (err.message === 'PHONE_SIGNUP_EMAIL_EXISTS') {
        setError('That email is already linked to an account. Leave it blank to continue — you can add it later.');
      } else {
        setError(err.serverMessage || 'Could not create your account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const setOtpDigit = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      setOtpDigits(prev => prev.map((d, i) => (i === index ? '' : d)));
      return;
    }
    // Handles both single keystrokes and a paste of the full code.
    const next = [...otpDigits];
    for (let i = 0; i < digits.length && index + i < OTP_LENGTH; i++) {
      next[index + i] = digits[i];
    }
    setOtpDigits(next);
    const lastFilled = Math.min(index + digits.length, OTP_LENGTH - 1);
    otpRefs.current[lastFilled]?.focus();
    if (next.every(d => d !== '')) {
      verifyOtp(next.join(''));
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="login-gate-overlay">
      <div className="login-gate-container">
        <div className="login-gate-modal">
          {/* Header */}
          <div className="login-gate-header">
            {view !== 'google' && (
              <button
                className="login-gate-back"
                onClick={() =>
                  switchView(
                    view === 'phone-otp' || view === 'phone-details' || view === 'phone-choose'
                      ? 'phone'
                      : 'google'
                  )
                }
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18l-6-6 6-6" stroke="#1D2129" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            <button className="login-gate-close" onClick={onClose}>
              <img src="/assets/close.svg" alt="Close" />
            </button>
          </div>

          {/* Content */}
          <div className="login-gate-content">
            {/* Logo */}
            <div className="login-gate-logo">
              <img src="/assets/MPG_Logo Full.svg" alt="My Pet Groomer Logo" />
            </div>

            {/* ── GOOGLE VIEW ── */}
            {view === 'google' && (
              <>
                <div className="login-gate-title">
                  <h2>{showAppointmentMessage ? 'Sign in to view appointments' : 'Sign in to continue'}</h2>
                  {showAppointmentMessage && (
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '8px', textAlign: 'center' }}>
                      You need to be logged in to view your appointments and manage bookings.
                    </p>
                  )}
                </div>
                <div className="login-gate-actions">
                  <LastUsedOption method="google" last={lastMethod}>
                    <GoogleLoginButton
                      currentStep={showAppointmentMessage ? 'appointments' : currentStep}
                      className="custom-google-login-button"
                    >
                      <div className="google-login-content">
                        <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <span>Continue with Google</span>
                      </div>
                    </GoogleLoginButton>
                  </LastUsedOption>

                  <div className="auth-divider">
                    <span>or</span>
                  </div>

                  <LastUsedOption method="phone" last={lastMethod}>
                    <button className="auth-btn-email-option" onClick={() => switchView('phone')}>
                      Continue with mobile number
                    </button>
                  </LastUsedOption>

                  <LastUsedOption method="email" last={lastMethod}>
                    <button className="auth-btn-email-option" onClick={() => switchView('email-login')}>
                      Continue with email
                    </button>
                  </LastUsedOption>
                </div>
              </>
            )}

            {/* ── PHONE NUMBER VIEW ── */}
            {view === 'phone' && (
              <>
                <div className="login-gate-title">
                  <h2>Continue with mobile</h2>
                  <p style={{ fontSize: '14px', color: '#666', marginTop: '8px', textAlign: 'center' }}>
                    We'll text you a one-time code to sign in — or to create your account if you're new.
                  </p>
                </div>
                <form className="auth-form" onSubmit={handlePhoneSubmit}>
                  <input
                    className="auth-input"
                    type="tel"
                    placeholder="Mobile number (e.g. 0434 123 456)"
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value)}
                    required
                    autoComplete="tel"
                    inputMode="tel"
                  />

                  {error && <div className="auth-error">{error}</div>}

                  <button className="auth-btn-primary" type="submit" disabled={loading || !phoneNumber.trim()}>
                    {loading ? 'Sending code…' : 'Send code'}
                  </button>
                </form>
              </>
            )}

            {/* ── PHONE OTP VIEW ── */}
            {view === 'phone-otp' && (
              <>
                <div className="login-gate-title">
                  <h2>Enter the code</h2>
                  <p style={{ fontSize: '14px', color: '#666', marginTop: '8px', textAlign: 'center' }}>
                    Sent to {formatMobileDisplay(phoneNumber)}. The code expires in 5 minutes.
                  </p>
                </div>
                <div className="auth-form">
                  <div className="auth-otp-row">
                    {otpDigits.map((digit, i) => (
                      <input
                        key={i}
                        ref={el => { otpRefs.current[i] = el; }}
                        className="auth-otp-box"
                        type="text"
                        inputMode="numeric"
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        maxLength={OTP_LENGTH}
                        value={digit}
                        onChange={e => setOtpDigit(i, e.target.value)}
                        onKeyDown={e => handleOtpKeyDown(i, e)}
                        disabled={loading}
                        aria-label={`Code digit ${i + 1}`}
                      />
                    ))}
                  </div>

                  {error && <div className="auth-error">{error}</div>}
                  {loading && <div className="auth-success">Verifying…</div>}

                  {resendIn > 0 ? (
                    <div className="auth-switch-row">
                      <span>Didn't get it? Resend in {resendIn}s</span>
                    </div>
                  ) : (
                    <div className="auth-switch-row">
                      <span>Didn't get it?</span>
                      <button type="button" className="auth-inline-link" onClick={requestPhoneCode} disabled={loading}>
                        Resend code
                      </button>
                    </div>
                  )}

                  <p style={{ fontSize: '12.5px', color: '#86909C', marginTop: '4px', textAlign: 'center', lineHeight: 1.5 }}>
                    Wrong number?{' '}
                    <button type="button" className="auth-inline-link" onClick={() => switchView('phone')}>
                      Edit it
                    </button>{' '}
                    or{' '}
                    <button type="button" className="auth-inline-link" onClick={() => switchView('email-login')}>
                      use email instead
                    </button>.
                  </p>
                </div>
              </>
            )}

            {/* ── WHICH OF YOU? (shared mobile, #904) ── */}
            {view === 'phone-choose' && (
              <>
                <div className="login-gate-title">
                  <h2>Who's booking?</h2>
                  <p style={{ fontSize: '14px', color: '#666', marginTop: '8px', textAlign: 'center' }}>
                    This number is shared by a few people. Tap your name so we
                    show the right pets.
                  </p>
                </div>
                <div className="auth-form">
                  {choices.map(choice => (
                    <button
                      key={choice.clientId}
                      type="button"
                      className="auth-submit-btn"
                      disabled={loading}
                      onClick={() => chooseClient(choice.clientId)}
                    >
                      {choice.firstName}
                    </button>
                  ))}
                  <p style={{ fontSize: '12.5px', color: '#86909C', marginTop: '4px', textAlign: 'center', lineHeight: 1.5 }}>
                    Not one of these?{' '}
                    <button type="button" className="auth-inline-link" onClick={() => switchView('phone')}>
                      Use a different number
                    </button>.
                  </p>
                </div>
              </>
            )}

            {/* ── PHONE SIGN-UP DETAILS VIEW ── */}
            {view === 'phone-details' && (
              <>
                <div className="login-gate-title">
                  <h2>Nearly there!</h2>
                  <p style={{ fontSize: '14px', color: '#666', marginTop: '8px', textAlign: 'center' }}>
                    Your number's verified — tell us who we're grooming for.
                  </p>
                </div>
                <form className="auth-form" onSubmit={handleCompleteSignup}>
                  <div className="auth-name-row">
                    <input
                      className="auth-input"
                      type="text"
                      placeholder="First name"
                      value={suFirstName}
                      onChange={e => setSuFirstName(e.target.value)}
                      required
                      autoComplete="given-name"
                      autoFocus
                    />
                    <input
                      className="auth-input"
                      type="text"
                      placeholder="Last name (optional)"
                      value={suLastName}
                      onChange={e => setSuLastName(e.target.value)}
                      autoComplete="family-name"
                    />
                  </div>
                  <input
                    className="auth-input"
                    type="email"
                    placeholder="Email (optional)"
                    value={suEmail}
                    onChange={e => setSuEmail(e.target.value)}
                    autoComplete="email"
                  />
                  <p style={{ fontSize: '12.5px', color: '#86909C', marginTop: '-4px', textAlign: 'center', lineHeight: 1.5 }}>
                    Email is used for booking confirmations &amp; receipts.
                  </p>

                  {/* MKT 16b: offer texts only on the client's own tick, never ticked for them (G27). */}
                  <label className="auth-offer-tick" data-signup-offer-texts>
                    <input type="checkbox" checked={suOfferTexts} onChange={e => setSuOfferTexts(e.target.checked)} />
                    <span className="auth-offer-tick-text">
                      <span className="auth-offer-tick-title">Text me offers from {salonName}</span>
                      <span className="auth-offer-tick-sub">Optional. You can opt out from any text.</span>
                    </span>
                  </label>

                  {error && <div className="auth-error">{error}</div>}

                  <button className="auth-btn-primary" type="submit" disabled={loading || !suFirstName.trim()}>
                    {loading ? 'Creating account…' : 'Create account'}
                  </button>
                  <p className="auth-offer-email-note">{salonName} may also email you offers. Every one has Unsubscribe.</p>
                </form>
              </>
            )}

            {/* ── EMAIL LOGIN VIEW ── */}
            {view === 'email-login' && (
              <>
                <div className="login-gate-title">
                  <h2>Sign in</h2>
                </div>
                <form className="auth-form" onSubmit={handleEmailLogin}>
                  <input
                    className="auth-input"
                    type="email"
                    placeholder="Email"
                    value={loginEmail}
                    onChange={e => setLoginEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                  <input
                    className="auth-input"
                    type="password"
                    placeholder="Password"
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />

                  {error === 'EMAIL_UNVERIFIED' ? (
                    <div className="auth-error">
                      Email not verified.{' '}
                      <button type="button" className="auth-inline-link" onClick={handleResendVerification}>
                        Resend verification email
                      </button>
                    </div>
                  ) : error ? (
                    <div className="auth-error">{error}</div>
                  ) : null}

                  {successMsg && <div className="auth-success">{successMsg}</div>}

                  <button className="auth-btn-primary" type="submit" disabled={loading}>
                    {loading ? 'Signing in…' : 'Sign in'}
                  </button>

                  <button type="button" className="auth-btn-link" onClick={() => switchView('forgot-password')}>
                    Forgot password?
                  </button>
                  <div className="auth-switch-row">
                    <span>Don't have an account?</span>
                    <button type="button" className="auth-inline-link" onClick={() => switchView('register')}>
                      Register
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* ── REGISTER VIEW ── */}
            {view === 'register' && (
              <>
                <div className="login-gate-title">
                  <h2>Create account</h2>
                </div>
                {successMsg ? (
                  <div className="auth-success-box">
                    <p>{successMsg}</p>
                    <button type="button" className="auth-btn-link" onClick={() => switchView('email-login')}>
                      Back to sign in
                    </button>
                  </div>
                ) : (
                  <form className="auth-form" onSubmit={handleRegister}>
                    <div className="auth-name-row">
                      <input
                        className="auth-input"
                        type="text"
                        placeholder="First name"
                        value={regFirstName}
                        onChange={e => setRegFirstName(e.target.value)}
                        required
                      />
                      <input
                        className="auth-input"
                        type="text"
                        placeholder="Last name"
                        value={regLastName}
                        onChange={e => setRegLastName(e.target.value)}
                        required
                      />
                    </div>
                    <input
                      className="auth-input"
                      type="email"
                      placeholder="Email"
                      value={regEmail}
                      onChange={e => setRegEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                    <input
                      className="auth-input"
                      type="password"
                      placeholder="Password"
                      value={regPassword}
                      onChange={e => setRegPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      minLength={8}
                    />

                    {error && <div className="auth-error">{error}</div>}

                    <button className="auth-btn-primary" type="submit" disabled={loading}>
                      {loading ? 'Creating account…' : 'Create account'}
                    </button>

                    <div className="auth-switch-row">
                      <span>Already have an account?</span>
                      <button type="button" className="auth-inline-link" onClick={() => switchView('email-login')}>
                        Sign in
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}

            {/* ── FORGOT PASSWORD VIEW ── */}
            {view === 'forgot-password' && (
              <>
                <div className="login-gate-title">
                  <h2>Reset password</h2>
                  <p style={{ fontSize: '14px', color: '#666', marginTop: '8px', textAlign: 'center' }}>
                    Enter your email and we'll send you a reset link.
                  </p>
                </div>
                {successMsg ? (
                  <div className="auth-success-box">
                    <p>{successMsg}</p>
                    <button type="button" className="auth-btn-link" onClick={() => switchView('email-login')}>
                      Back to sign in
                    </button>
                  </div>
                ) : (
                  <form className="auth-form" onSubmit={handleForgotPassword}>
                    <input
                      className="auth-input"
                      type="email"
                      placeholder="Email"
                      value={fpEmail}
                      onChange={e => setFpEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                    <button className="auth-btn-primary" type="submit" disabled={loading}>
                      {loading ? 'Sending…' : 'Send reset link'}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginGate;
